import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserCog, Shield, GraduationCap, Database,
  BarChart3, Bell, FileText, Upload, CalendarClock, ChevronRight, ChevronLeft, GraduationCap as Logo, Plug, RadioTower, LayoutTemplate, Send, UserCheck
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { MENU_CODE_ALIASES } from '../../constants';

const MENU_ITEMS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'leads', label: 'Leads', icon: Users },
  { key: 'counsellors', label: 'Counsellor View', icon: UserCheck },
  { key: 'followups', label: 'Follow-ups', icon: CalendarClock },
  { key: 'reports', label: 'Reports', icon: BarChart3 },
  { key: 'csv', label: 'CSV Import', icon: Upload },
  { key: 'users', label: 'Users', icon: UserCog },
  { key: 'roles', label: 'Roles', icon: Shield },
  { key: 'courses', label: 'Courses', icon: GraduationCap },
  { key: 'masters', label: 'Master Data', icon: Database },
  { key: 'notifications', label: 'Notifications', icon: Bell },
  { key: 'audit', label: 'Audit Logs', icon: FileText },
  { key: 'integrations', label: 'Integrations', icon: Plug },
  { key: 'lead-sync', label: 'Lead Sync', icon: RadioTower },
  { key: 'templates', label: 'Templates', icon: LayoutTemplate },
  { key: 'bulk-campaigns', label: 'Bulk Campaigns', icon: Send },
];

const getMenusForRole = (role) => {
  const roleMenus = role?.menus;
  
  const getStaticMenus = (code) => {
    switch (code) {
      case 'SUPER_ADMIN': return MENU_ITEMS.map(m => m.key);
      case 'RETAIL_OU_LEAD': return ['dashboard', 'leads', 'followups', 'counsellors', 'reports', 'csv', 'users', 'courses', 'masters', 'notifications', 'lead-sync', 'templates', 'bulk-campaigns'];
      case 'COUNSELLOR': return ['dashboard', 'leads', 'followups', 'notifications', 'bulk-campaigns'];
      case 'AUDITOR': return ['dashboard', 'leads', 'reports', 'audit'];
      default: return null;
    }
  };

  const staticMenus = getStaticMenus(role?.code);

  if (Array.isArray(roleMenus) && roleMenus.length && typeof roleMenus[0] === 'object') {
    let keys = roleMenus
      .map(m => MENU_CODE_ALIASES[m.code] || m.code)
      .filter(key => MENU_ITEMS.some(item => item.key === key));
    
    if ((role?.code === 'RETAIL_OU_LEAD' || role?.code === 'SUPER_ADMIN') && !keys.includes('lead-sync')) {
      keys.push('lead-sync');
    }
    if (role?.code === 'RETAIL_OU_LEAD' && !keys.includes('templates')) {
      keys.push('templates');
    }
    if ((role?.code === 'RETAIL_OU_LEAD' || role?.code === 'COUNSELLOR') && !keys.includes('bulk-campaigns')) {
      keys.push('bulk-campaigns');
    }
    if ((role?.code === 'RETAIL_OU_LEAD' || role?.code === 'SUPER_ADMIN') && !keys.includes('counsellors')) {
      keys.push('counsellors');
    }

    if (staticMenus) {
      keys = keys.filter(key => staticMenus.includes(key));
    }
    
    return keys.includes('dashboard') ? keys : ['dashboard', ...keys];
  }
  
  return staticMenus || ['dashboard'];
};

const Sidebar = ({ collapsed, onToggle }) => {
  const { dark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const activePage = location.pathname.split('/')[1] || 'dashboard';
  const allowedMenus = getMenusForRole(user?.role);
  const visibleMenus = MENU_ITEMS.filter(m => allowedMenus.includes(m.key));

  const bgSidebar = dark ? '#1E293B' : '#FFFFFF';
  const txtPrimary = dark ? '#F1F5F9' : '#0F172A';
  const txtSecondary = dark ? '#94A3B8' : '#64748B';

  return (
    <div style={{
      width: collapsed ? 64 : 240,
      minWidth: collapsed ? 64 : 240,
      background: bgSidebar,
      borderRight: `1px solid ${dark ? '#334155' : '#E2E8F0'}`,
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.2s, min-width 0.2s',
      overflow: 'hidden',
    }}>
      {/* Logo */}
      <div style={{
        padding: collapsed ? '16px 12px' : '16px 20px',
        borderBottom: `1px solid ${dark ? '#334155' : '#E2E8F0'}`,
        display: 'flex', alignItems: 'center', gap: 10, minHeight: 60,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: 'linear-gradient(135deg,#3B82F6,#8B5CF6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Logo size={18} color="#fff" />
        </div>
        {!collapsed && (
          <span style={{ fontSize: 15, fontWeight: 700, color: txtPrimary, whiteSpace: 'nowrap' }}>
            CourseLead CRM
          </span>
        )}
      </div>

      {/* Nav Items */}
      <nav style={{ flex: 1, padding: '8px', overflowY: 'auto' }}>
        {visibleMenus.map(item => {
          const Icon = item.icon;
          const isActive = activePage === item.key;
          return (
            <button key={item.key} onClick={() => navigate(`/${item.key}`)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10,
              padding: collapsed ? '10px' : '9px 14px',
              borderRadius: 8, border: 'none', cursor: 'pointer', marginBottom: 2,
              background: isActive ? (dark ? '#334155' : '#EFF6FF') : 'transparent',
              color: isActive ? '#3B82F6' : txtSecondary,
              fontWeight: isActive ? 600 : 500, fontSize: 13,
              justifyContent: collapsed ? 'center' : 'flex-start',
              transition: 'all 0.15s',
            }}>
              <Icon size={18} />
              {!collapsed && <span style={{ whiteSpace: 'nowrap' }}>{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Collapse Toggle */}
      <div style={{ padding: '12px', borderTop: `1px solid ${dark ? '#334155' : '#E2E8F0'}` }}>
        <button onClick={onToggle} style={{
          width: '100%', padding: '8px', borderRadius: 6, border: 'none',
          background: 'transparent', color: txtSecondary, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
